import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { parseProgrammeLevel } from '../lib/programmeView.js';
import type { ProgrammeLevelFilter } from '../types/programme.js';

const OPTIONS: ReadonlyArray<{ value: ProgrammeLevelFilter; label: string }> = [
  { value: 'all', label: 'All levels' },
  { value: 'I', label: 'Level I' },
  { value: 'II', label: 'Level II' },
  { value: 'III', label: 'Level III' },
];

/**
 * Programme-only exam-level control. It writes `?level=I|II|III|all` and never
 * the remembered course: `CourseLevelSelector` persists a concrete course via
 * `buildCourseSwitchTarget`, which does not know `/programme` and would send
 * the reviewer to `/queue`. An `all` sentinel written into that memory would
 * contaminate every other tab and the structural badge's fetch.
 */
export function ProgrammeLevelSelector() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const level = parseProgrammeLevel(searchParams.get('level'));

  function switchTo(next: ProgrammeLevelFilter) {
    if (next === level) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.set('level', next);
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }

  return (
    <div
      className="flex shrink-0 items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5"
      data-testid="shell-programme-identity"
      title="This page is sliced by CFA exam level, not by a single authoring course."
    >
      <label className="flex shrink-0 items-center gap-2">
        <span className="text-[0.65rem] font-bold uppercase tracking-[0.08em] text-slate-500">
          Level
        </span>
        <select
          aria-label="Programme exam level"
          className="max-w-[18rem] cursor-pointer truncate rounded-md border border-slate-300 bg-white py-1 pl-2 pr-7 text-sm font-bold text-slate-800 focus:border-primary focus:ring-1 focus:ring-primary"
          data-testid="programme-level-select"
          onChange={(event) => switchTo(parseProgrammeLevel(event.target.value))}
          value={level}
        >
          {OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
